import {
    Box,
    HStack,
    Menu,
    MenuButton,
    Button as ChakraButton,
    Portal,
    Tooltip,
} from '@chakra-ui/react';
import { Button, MenuList, MenuItem } from 'components/v4';
import { useLayoutEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
    BUTTON_GROUP_MENU_MIN_WIDTH,
    getButtonGroupFullWidth,
} from './collapsibleButtonGroupSizing';

/**
 * A row of toolbar buttons that collapses into a single labeled dropdown
 * (mirroring PlatformFilterStack's collapse-to-stack behavior) once the
 * full row of buttons no longer fits the available toolbar width. Each
 * `items` entry:
 *   { key, label, icon: Component, onClick, colorScheme?, variant?, tooltip? }
 *
 * By default this measures its own available width and decides for itself.
 * Pass `forceOverflowing` (a boolean) when a parent toolbar coordinates this
 * component's collapse state alongside a sibling (e.g. PlatformFilterStack)
 * that shares the same row — in that case this component skips its own
 * measurement and simply renders whichever state the parent has decided.
 */
const CollapsibleButtonGroup = ({
    items,
    menuLabel = 'Actions',
    forceOverflowing,
    ...boxProps
}) => {
    const { colorScheme } = useSelector((state) => state.settings);
    const containerRef = useRef(null);
    const [autoOverflowing, setAutoOverflowing] = useState(false);
    const isControlled = forceOverflowing !== undefined;
    const isOverflowing = isControlled ? forceOverflowing : autoOverflowing;

    const visibleItems = items?.filter(Boolean) ?? [];

    useLayoutEffect(() => {
        if (isControlled) {
            return undefined;
        }

        const container = containerRef.current;
        if (!container || !visibleItems.length) {
            setAutoOverflowing(false);
            return undefined;
        }

        const neededWidth = getButtonGroupFullWidth(visibleItems.length);

        const checkWidth = () => {
            const availableWidth = container.clientWidth;
            // A width of 0 means the container hasn't been laid out yet
            // (or layout isn't available, e.g. in jsdom) — skip the check
            // rather than falsely collapsing.
            if (availableWidth <= 0) return;
            setAutoOverflowing(availableWidth < neededWidth);
        };

        checkWidth();

        const resizeObserver = new ResizeObserver(checkWidth);
        resizeObserver.observe(container);
        return () => resizeObserver.disconnect();
    }, [visibleItems.length, isControlled]);

    if (!visibleItems.length) {
        return null;
    }

    return (
        <Box
            ref={containerRef}
            data-testid="collapsible-button-group"
            flexGrow={0}
            flexShrink={isControlled ? 0 : 1}
            minWidth={
                isControlled ? undefined : `${BUTTON_GROUP_MENU_MIN_WIDTH}px`
            }
            overflow="hidden"
            {...boxProps}>
            {isOverflowing ? (
                <Menu>
                    <MenuButton
                        as={ChakraButton}
                        size="sm"
                        colorScheme={colorScheme}>
                        {menuLabel}
                    </MenuButton>
                    {/* Portalled so this component's own overflow:hidden
                        (and the toolbar's) can't clip the dropdown. */}
                    <Portal>
                        <MenuList zIndex={1000}>
                            {visibleItems.map((item) => (
                                <MenuItem
                                    key={item.key}
                                    icon={item.icon ? <item.icon /> : null}
                                    onClick={item.onClick}>
                                    {item.label}
                                </MenuItem>
                            ))}
                        </MenuList>
                    </Portal>
                </Menu>
            ) : (
                <HStack spacing={2}>
                    {visibleItems.map((item) => (
                        <Tooltip
                            key={item.key}
                            label={item.tooltip ?? item.label}
                            placement="bottom"
                            hasArrow
                            shouldWrapChildren>
                            <Button
                                size="sm"
                                width="110px"
                                aria-label={item.ariaLabel}
                                colorScheme={item.colorScheme}
                                variant={item.variant}
                                onClick={item.onClick}
                                data-walkthroughid={item.walkthroughId}>
                                {item.label}
                            </Button>
                        </Tooltip>
                    ))}
                </HStack>
            )}
        </Box>
    );
};

export default CollapsibleButtonGroup;
